import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { notifyPermissionError, useNotification } from '@1823-partners/core';
import dayjs from 'dayjs';

import { fetchSecurityFormFields, loadDeal } from '../api';

const choiceValue = choice => (
  typeof choice === 'object' ? (choice.value ?? choice.id ?? choice.label ?? '') : choice
);

const findField = (fields, ids) => fields.find(field => ids.includes(field.id));

const normalizedCusips = value => (Array.isArray(value) ? value : [value])
  .map(cusip => (cusip == null ? '' : String(cusip).trim()))
  .filter(Boolean);

const NewComparisonDealModal = memo(({ open, onClose, onSelect, modifiedDate }) => {
  const { showNotification } = useNotification();
  const [fields, setFields] = useState([]);
  const [dealName, setDealName] = useState('');
  const [password, setPassword] = useState('');
  const [loadedDeal, setLoadedDeal] = useState(null);
  const [trancheMap, setTrancheMap] = useState({});
  const [selectedTranches, setSelectedTranches] = useState([]);
  const [assetType, setAssetType] = useState('');
  const [securityOwner, setSecurityOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loadRequestIdRef = useRef(0);

  const invalidateLoadedDeal = useCallback(() => {
    loadRequestIdRef.current += 1;
    setLoading(false);
    setLoadedDeal(null);
    setTrancheMap({});
    setSelectedTranches([]);
  }, []);

  useEffect(() => {
    loadRequestIdRef.current += 1;
    setLoading(false);
    if (!open) return;

    setDealName('');
    setPassword('');
    setLoadedDeal(null);
    setTrancheMap({});
    setSelectedTranches([]);
    setAssetType('');
    setSecurityOwner('');
    setError('');

    fetchSecurityFormFields()
      .then(response => setFields(response.data?.fields || []))
      .catch(error => {
        setFields([]);
        notifyPermissionError(showNotification, error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const typeField = useMemo(
    () => findField(fields, ['TYPE']),
    [fields]
  );
  const ownerField = useMemo(
    () => findField(fields, ['SEC_OWNER', 'SECURITY_OWNER']),
    [fields]
  );
  const typeChoices = useMemo(
    () => (typeField?.choices || []).map(choiceValue).filter(Boolean),
    [typeField]
  );
  const ownerChoices = useMemo(
    () => (ownerField?.choices || []).map(choiceValue).filter(Boolean),
    [ownerField]
  );

  const handleLoad = useCallback(async () => {
    const normalizedDealName = dealName.trim();
    if (!normalizedDealName) {
      setError('Enter a deal name first.');
      return;
    }

    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const response = await loadDeal(
        normalizedDealName,
        password.trim() || null,
        null,
        dayjs(modifiedDate).format('YYYY-MM-DD')
      );
      if (requestId !== loadRequestIdRef.current) return;

      const data = typeof response.data === 'string' ? JSON.parse(response.data) : (response.data || {});
      const nextTrancheMap = Object.fromEntries(
        Object.entries(data.tranche_map || {})
          .map(([tranche, cusips]) => [tranche, normalizedCusips(cusips)])
          .filter(([, cusips]) => cusips.length > 0)
      );
      const tranches = Object.keys(nextTrancheMap);
      if (tranches.length === 0) {
        throw new Error('INTEX returned no tranches with usable CUSIPs for this deal.');
      }

      setLoadedDeal(data);
      setTrancheMap(nextTrancheMap);
      setSelectedTranches([]);
      setAssetType(String(data.type || data.TYPE || typeField?.default || ''));
      setSecurityOwner(String(
        data.security_owner
        || data.SECURITY_OWNER
        || data.SEC_OWNER
        || ownerField?.default
        || ''
      ));
    } catch (loadError) {
      if (requestId !== loadRequestIdRef.current) return;

      setLoadedDeal(null);
      setTrancheMap({});
      setSelectedTranches([]);
      setError(loadError?.response?.data?.message || loadError?.message || 'Unable to load the INTEX deal.');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [dealName, modifiedDate, ownerField, password, typeField]);

  const handleUseDeal = () => {
    if (!assetType) {
      setError('Asset type is required before this deal can be used.');
      return;
    }
    if (selectedTranches.length === 0) {
      setError('Select at least one tranche.');
      return;
    }

    const selectedMap = Object.fromEntries(
      selectedTranches.map(tranche => [tranche, normalizedCusips(trancheMap[tranche])])
    );
    const condensedPairs = selectedTranches.flatMap(tranche => {
      return selectedMap[tranche].map(cusip => ({ cusip, tranche }));
    });
    const defaults = Object.fromEntries(
      fields
        .filter(field => field.default !== undefined)
        .map(field => [field.label || field.id, field.default])
    );
    const normalizedDealName = dealName.trim();

    onSelect({
      ...defaults,
      ...(loadedDeal || {}),
      'Deal Name': normalizedDealName,
      Type: assetType,
      'Security Owner': securityOwner,
      Tranche: condensedPairs.map(({ tranche }) => tranche).join(','),
      CUSIP: condensedPairs.map(({ cusip }) => cusip).join(','),
      ASOFDT: dayjs(modifiedDate).format('YYYY-MM-DD'),
      tranche_map: selectedMap,
    });
    onClose();
  };

  const renderChoiceField = (label, value, setValue, choices, required = false) => {
    const labelId = `new-comparison-${label.toLowerCase().replace(/\s+/g, '-')}-label`;
    return choices.length > 0 ? (
      <FormControl fullWidth size="small" required={required} error={required && loadedDeal && !value}>
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          labelId={labelId}
          required={required}
          value={value}
          label={label}
          onChange={event => setValue(event.target.value)}
        >
          {choices.map(choice => <MenuItem key={choice} value={choice}>{choice}</MenuItem>)}
        </Select>
      </FormControl>
    ) : (
      <TextField
        fullWidth
        size="small"
        required={required}
        label={label}
        value={value}
        onChange={event => setValue(event.target.value)}
        error={required && Boolean(loadedDeal) && !value}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: '17px', fontWeight: 750 }}>Load a new INTEX deal</Typography>
            <Typography variant="caption" color="text.secondary">
              Use the deal in this comparison without saving it to Trade.
            </Typography>
          </Box>
          <IconButton aria-label="Close new deal loader" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            required
            fullWidth
            size="small"
            label="Deal name"
            value={dealName}
            onChange={event => {
              setDealName(event.target.value);
              invalidateLoadedDeal();
            }}
            helperText="Enter the deal name exactly as it appears in INTEX."
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label="Deal password (optional)"
            value={password}
            onChange={event => {
              setPassword(event.target.value);
              invalidateLoadedDeal();
            }}
          />
          <Button
            variant={loadedDeal ? 'outlined' : 'contained'}
            onClick={handleLoad}
            disabled={loading || !dealName.trim()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Loading from INTEX…' : loadedDeal ? 'Reload INTEX deal' : 'Load INTEX deal'}
          </Button>

          {loadedDeal && (
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Comparison details</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {renderChoiceField('Asset type', assetType, setAssetType, typeChoices, true)}
                <Autocomplete
                  fullWidth
                  freeSolo
                  size="small"
                  options={ownerChoices}
                  value={securityOwner || null}
                  inputValue={securityOwner}
                  onChange={(event, value) => setSecurityOwner(value || '')}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setSecurityOwner(value);
                    }
                  }}
                  renderInput={params => <TextField {...params} label="Security owner" />}
                />
              </Stack>
              <FormControl fullWidth required size="small">
                <InputLabel id="new-comparison-tranches-label">Tranches</InputLabel>
                <Select
                  labelId="new-comparison-tranches-label"
                  label="Tranches"
                  multiple
                  value={selectedTranches}
                  onChange={event => setSelectedTranches(event.target.value)}
                  renderValue={selected => `${selected.length} tranche${selected.length === 1 ? '' : 's'} selected`}
                >
                  {Object.keys(trancheMap).map(tranche => (
                    <MenuItem key={tranche} value={tranche}>{tranche}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUseDeal}
          disabled={!loadedDeal || !assetType || selectedTranches.length === 0}
        >
          Use deal in comparison
        </Button>
      </DialogActions>
    </Dialog>
  );
});

NewComparisonDealModal.displayName = 'NewComparisonDealModal';

export default NewComparisonDealModal;
